import React, { Component } from "react";
import { connect } from "react-redux";
import { pmaActions } from "_store/_actions";
import { GetOAuthToken, IsValidSessionSwitch } from "common/storage";
import { isUserAdmin } from "common/roles/role";

import SentenceTrendsHeader from "./SentenceTrendsHeader";
import SentenceTrendsBody from "./SentenceTrendsBody";
import SentenceTrendsEditorModal from "./SentenceTrendsEditorModal";
import QVFourEmailAndWebsiteFooter from "views/Pma/PmaEditor/SecondPageComponents/QVFourEmailAndWebsiteFooter";

import {
  pickTrends2WatchRowForArea,
  rowToHeaderLines,
  hydrateT2wEditableFields,
  buildSavedTrendsRow,
  mergeTrendsRowIntoList,
  isTrendsFormDirty,
  revertTrendsFormToOriginal,
  isTrendsFormAtOriginal,
  isTrendsSectionEdited,
  T2W_FIELD_GROUPS,
  T2W_EDITABLE_FIELDS,
  isTrendsFooterOn,
  isTrendsFormOverLimit,
} from "./tpTrends2WatchUtils";

const TREND_MODE_BY_INDEX = {
  1: "trend1",
  2: "trend2",
  3: "trend3",
};

const isAdminSessionAccess = () => {
  try {
    const token = GetOAuthToken();
    const oauthData = token ? JSON.parse(token) : null;
    return (
      (oauthData && isUserAdmin(oauthData.roleID)) || IsValidSessionSwitch()
    );
  } catch (err) {
    return IsValidSessionSwitch();
  }
};

/**
 * Second-page “3 trends” dashboard: copy comes from `tpTrends2WatchList`
 * (getPMADetails), filtered by MLS area. Editor saves via
 * setTPTrends2WatchForAgent with listgenUserID and the full area array.
 */
class SentenceTrends extends Component {
  state = {
    t2wDraft: null,
    modalOpen: false,
    modalMode: null,
    editFormData: null,
    editFormBaseline: null,
    saving: false,
    saveError: null,
    saveSuccess: null,
  };

  saveSuccessTimeoutId = null;

  componentDidMount() {
    require("./print.scss");
    if (this.props.fromEditor) {
      require("./editor.scss");
    }
    this.syncT2wDraftFromProps();
  }

  componentWillUnmount() {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
      this.saveSuccessTimeoutId = null;
    }
  }

  componentDidUpdate(prevProps) {
    const { tpTrends2WatchList, currentArea } = this.props;
    const prevList = prevProps.tpTrends2WatchList;
    const prevAreaId = prevProps.currentArea?.mlsAreaID;
    const areaId = currentArea?.mlsAreaID;
    if (tpTrends2WatchList !== prevList || areaId !== prevAreaId) {
      if (areaId !== prevAreaId) {
        this.setState({
          modalOpen: false,
          modalMode: null,
          editFormData: null,
          editFormBaseline: null,
          saveError: null,
          saveSuccess: null,
        });
      }
      this.syncT2wDraftFromProps();
    }
  }

  syncT2wDraftFromProps = () => {
    const { tpTrends2WatchList, currentArea } = this.props;
    const row = pickTrends2WatchRowForArea(
      tpTrends2WatchList,
      currentArea?.mlsAreaID,
    );
    if (!row && this.state.t2wDraft) {
      return;
    }
    this.setState({
      t2wDraft: row ? { ...row } : null,
    });
  };

  getActiveGroupFields = (mode = this.state.modalMode) => {
    return T2W_FIELD_GROUPS[mode]?.fields || [];
  };

  openEditModal = (mode) => {
    const { t2wDraft } = this.state;
    if (!t2wDraft || !T2W_FIELD_GROUPS[mode]) return;
    const hydrated = hydrateT2wEditableFields(t2wDraft);
    this.setState({
      modalOpen: true,
      modalMode: mode,
      editFormData: hydrated,
      editFormBaseline: { ...hydrated },
      saving: false,
      saveError: null,
      saveSuccess: null,
    });
  };

  closeEditModal = () => {
    if (this.state.saving || this.state.saveSuccess) return;
    this.setState({
      modalOpen: false,
      modalMode: null,
      editFormData: null,
      editFormBaseline: null,
      saveError: null,
    });
  };

  handleCancel = () => {
    this.closeEditModal();
  };

  handleResetToDefault = () => {
    this.setState((prev) => ({
      editFormData: revertTrendsFormToOriginal(
        prev.editFormData,
        this.getActiveGroupFields(prev.modalMode),
      ),
      saveError: null,
    }));
  };

  handleEditFieldChange = (key, value) => {
    if (typeof key !== "string" || key.startsWith("org")) return;
    this.setState((prev) => ({
      editFormData: prev.editFormData
        ? { ...prev.editFormData, [key]: value }
        : null,
      saveError: null,
    }));
  };

  showSaveSuccess = () => {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
    }
    this.setState({ saveSuccess: true, saveError: null, saving: false });
    this.saveSuccessTimeoutId = setTimeout(() => {
      this.setState({
        modalOpen: false,
        modalMode: null,
        editFormData: null,
        editFormBaseline: null,
        saveSuccess: null,
      });
      this.saveSuccessTimeoutId = null;
    }, 1600);
  };

  onSave = async () => {
    const { editFormData, t2wDraft, saving, saveSuccess, editFormBaseline } =
      this.state;
    const { tpTrends2WatchList, setTPTrends2WatchForAgent, agentData } =
      this.props;
    const groupFields = this.getActiveGroupFields();
    if (
      !editFormData ||
      !t2wDraft ||
      saving ||
      saveSuccess ||
      groupFields.length === 0 ||
      !isTrendsFormDirty(editFormBaseline, editFormData, groupFields)
    ) {
      return;
    }

    if (isTrendsFormOverLimit(editFormData, groupFields)) {
      this.setState({
        saveError:
          "Unable to save trends: one or more fields exceed the character limit.",
      });
      return;
    }

    const listgenUserID = agentData?.listgenUserID;
    if (!listgenUserID) {
      this.setState({
        saveError: "Unable to save trends: missing listgenUserID.",
      });
      return;
    }

    const edits = {};
    T2W_EDITABLE_FIELDS.forEach(({ key, kind }) => {
      if (kind === "footerToggle") {
        edits[key] = isTrendsFooterOn(editFormData[key])
          ? editFormData[key]
          : null;
      } else {
        edits[key] = editFormData[key] ?? "";
      }
    });

    const savedRow = buildSavedTrendsRow(t2wDraft, edits, { listgenUserID });
    const nextList = mergeTrendsRowIntoList(tpTrends2WatchList, savedRow);

    this.setState({ saving: true, saveError: null });
    const result = await setTPTrends2WatchForAgent(nextList, listgenUserID);
    if (result?.ok) {
      this.setState({ t2wDraft: savedRow });
      this.showSaveSuccess();
      return;
    }
    this.setState({
      saving: false,
      saveError: result?.message || "Unable to save trends. Please try again.",
    });
  };

  render() {
    const {
      agentData,
      currentArea,
      hoData,
      printVersion,
      fromEditor,
      showAllEditButtons,
    } = this.props;

    const {
      t2wDraft,
      modalOpen,
      modalMode,
      editFormData,
      editFormBaseline,
      saving,
      saveError,
      saveSuccess,
    } = this.state;

    const { main: titleMain, sub: titleSub } = rowToHeaderLines(t2wDraft);
    const contextTitle = [
      titleMain != null && String(titleMain).trim() !== ""
        ? titleMain
        : "3 Trends to Watch",
      titleSub != null && String(titleSub).trim() !== ""
        ? titleSub
        : "in Today's Market",
    ]
      .map((part) => String(part).trim())
      .filter(Boolean)
      .join(" ");

    const canEditT2w =
      Boolean(t2wDraft) &&
      fromEditor &&
      (isAdminSessionAccess() ||
        (Boolean(showAllEditButtons) &&
          currentArea?.mailingStatus !== "Approved"));

    const activeGroup = T2W_FIELD_GROUPS[modalMode];
    const activeFields = activeGroup?.fields || [];

    return (
      <div
        className={`${printVersion ? "print" : "editor"}-qv4-second-left sentence-trends-page`}
      >
        <div
          className={`print-sentence-trends ${fromEditor ? "from-editor" : ""}`}
        >
          <img
            src="https://d2onbxnrte2nim.cloudfront.net/non-covers/072526_Clouds_cool-2-1.jpg"
            alt=""
            className="dashboard-bg"
          />

          <SentenceTrendsHeader
            titleMain={titleMain}
            titleSub={titleSub}
            showEditButton={canEditT2w}
            onEditClick={() => this.openEditModal("header")}
            isEdited={isTrendsSectionEdited(
              t2wDraft,
              T2W_FIELD_GROUPS.header.fields,
            )}
            editDisabled={saving}
          />
          <SentenceTrendsBody
            currentArea={currentArea}
            hoData={hoData}
            printVersion={printVersion}
            agentData={agentData}
            t2wDraft={t2wDraft}
            fromEditor={fromEditor}
            showEditButtons={canEditT2w}
            onEditTrend={(index) =>
              this.openEditModal(TREND_MODE_BY_INDEX[index])
            }
            onEditTakeaway={() => this.openEditModal("takeaway")}
            editedTrendFlags={{
              1: isTrendsSectionEdited(
                t2wDraft,
                T2W_FIELD_GROUPS.trend1.fields,
              ),
              2: isTrendsSectionEdited(
                t2wDraft,
                T2W_FIELD_GROUPS.trend2.fields,
              ),
              3: isTrendsSectionEdited(
                t2wDraft,
                T2W_FIELD_GROUPS.trend3.fields,
              ),
            }}
            isTakeawayEdited={isTrendsSectionEdited(
              t2wDraft,
              T2W_FIELD_GROUPS.takeaway.fields,
            )}
            editDisabled={saving}
          />
        </div>
        <QVFourEmailAndWebsiteFooter
          agentData={agentData}
          currentArea={currentArea}
          printVersion={printVersion}
        />
        {canEditT2w ? (
          <SentenceTrendsEditorModal
            open={modalOpen}
            title={activeGroup?.title}
            headerVariant={activeGroup?.headerVariant || "default"}
            modalMode={modalMode}
            contextTitle={contextTitle}
            neighborhood={currentArea?.mlsNeighborhood || ""}
            fields={activeFields}
            formData={editFormData}
            isDirty={isTrendsFormDirty(
              editFormBaseline,
              editFormData,
              activeFields,
            )}
            onFieldChange={this.handleEditFieldChange}
            onClose={this.handleCancel}
            onSave={this.onSave}
            onResetToDefault={this.handleResetToDefault}
            canResetToDefault={
              !isTrendsFormAtOriginal(editFormData, activeFields)
            }
            saving={saving}
            saveSuccess={Boolean(saveSuccess)}
            saveError={saveError}
          />
        ) : null}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  setTPTrends2WatchForAgent: (tpTrends2WatchList, listgenUserID) =>
    dispatch(
      pmaActions.setTPTrends2WatchForAgent(tpTrends2WatchList, listgenUserID),
    ),
});

export default connect(null, mapDispatchToProps)(SentenceTrends);
